import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, ScrollText } from 'lucide-react'
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns'
import { Badge, Button, Card } from '@/components/ui'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { PermissionGate } from '@/components/common/PermissionGate'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import type { AuditLog, AuditLogQueryParams } from '@/types'
import {
  useAuditLogs,
  useAuditLog,
  useExportAuditLogsCsv,
} from '../hooks/useAuditLogs'
import { AuditLogFilters, type AuditLogFilterKey, type DatePreset } from './AuditLogFilters'
import { AuditLogList } from './AuditLogList'
import { AuditLogDetailModal } from './AuditLogDetailModal'
import { AuditLogStats } from './AuditLogStats'

const DEFAULT_LIMIT = 10
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

export function AuditLogsPage(): React.ReactElement {
  const [searchParams] = useSearchParams()

  const [params, setParams] = useState<AuditLogQueryParams | undefined>(() => {
    const initial: AuditLogQueryParams = { page: 1, limit: DEFAULT_LIMIT }
    const module = searchParams.get('module')
    const entityId = searchParams.get('entityId')
    const entityType = searchParams.get('entityType')
    const companyId = searchParams.get('companyId')
    if (module) initial.module = module
    if (entityId) initial.entityId = entityId
    if (entityType) initial.entityType = entityType
    if (companyId) initial.companyId = companyId
    return initial
  })
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useAuditLogs(params)
  const { data: selectedLog, isLoading: detailLoading, error: detailError } = useAuditLog(selectedId)
  const exportMutation = useExportAuditLogsCsv()

  const logs = Array.isArray(data?.data) ? (data as { data: AuditLog[] }).data : []
  const total = data?.total ?? 0
  const page = data?.page ?? 1
  const limit = data?.limit ?? DEFAULT_LIMIT
  const totalPages = data?.totalPages ?? 0

  const handleApplyFilters = useCallback((next: AuditLogQueryParams) => {
    setParams({ ...next, page: 1, limit: next.limit ?? DEFAULT_LIMIT })
  }, [])

  const handleResetFilters = useCallback(() => {
    setParams({ page: 1, limit: DEFAULT_LIMIT })
  }, [])

  const handlePresetApply = useCallback((preset: DatePreset) => {
    const now = new Date()
    let from: Date
    let to: Date

    switch (preset) {
      case 'today':
        from = startOfDay(now)
        to = endOfDay(now)
        break
      case 'week':
        from = startOfWeek(now, { weekStartsOn: 1 })
        to = endOfWeek(now, { weekStartsOn: 1 })
        break
      case 'month':
        from = startOfMonth(now)
        to = endOfMonth(now)
        break
      case 'lastMonth': {
        const last = subMonths(now, 1)
        from = startOfMonth(last)
        to = endOfMonth(last)
        break
      }
    }

    const dateFrom = `${from.toISOString().slice(0, 10)}T00:00:00.000Z`
    const dateTo = `${to.toISOString().slice(0, 10)}T23:59:59.999Z`

    setParams((prev) => ({
      ...(prev ?? { page: 1, limit: DEFAULT_LIMIT }),
      page: 1,
      dateFrom,
      dateTo,
    }))
  }, [])

  const handlePageChange = useCallback(
    (nextPage: number) => {
      setParams((prev) => (prev ? { ...prev, page: nextPage } : { page: nextPage, limit: DEFAULT_LIMIT }))
    },
    []
  )

  const handleLimitChange = useCallback((newLimit: number) => {
    setParams((prev) => ({
      ...(prev ?? { page: 1, limit: DEFAULT_LIMIT }),
      page: 1,
      limit: newLimit,
    }))
  }, [])

  const handleViewDetail = useCallback((log: AuditLog) => {
    setSelectedId(log._id)
  }, [])

  const handleCloseDetail = useCallback(() => {
    setSelectedId(null)
  }, [])

  const handleExportCsv = useCallback(() => {
    exportMutation.mutate(params)
  }, [params, exportMutation])

  const handleRemoveFilter = useCallback((key: AuditLogFilterKey) => {
    setParams((prev) => {
      if (!prev) return { page: 1, limit: DEFAULT_LIMIT }
      const next: AuditLogQueryParams = { page: 1, limit: prev.limit ?? DEFAULT_LIMIT }
      if (key !== 'action' && prev.action) next.action = prev.action
      if (key !== 'module' && prev.module) next.module = prev.module
      if (key !== 'actorId' && prev.actorId) next.actorId = prev.actorId
      if (key !== 'entityType' && prev.entityType) next.entityType = prev.entityType
      if (key !== 'entityId' && prev.entityId) next.entityId = prev.entityId
      if (key !== 'isFinancial' && prev.isFinancial) next.isFinancial = true
      if (key !== 'dateFrom' && prev.dateFrom) next.dateFrom = prev.dateFrom
      if (key !== 'dateTo' && prev.dateTo) next.dateTo = prev.dateTo
      return next
    })
  }, [])

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Organization', to: '/dashboard' },
        { label: 'Audit logs' },
      ]}
      icon={ScrollText}
      title="Audit logs"
      description="View and export audit trail for actions across the system."
      className="gap-0"
      hideHeader
    >
    <div className="space-y-4">
      <WorkspaceToolbar
        actions={
          <PermissionGate module="audit" action="export">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleExportCsv}
              loading={exportMutation.isPending}
            >
              <Download className="h-4 w-4 shrink-0" />
              <span className="ml-1.5">
                Export CSV
              </span>
            </Button>
          </PermissionGate>
        }
      />

      {/* Summary stat cards */}
      <AuditLogStats total={total} logs={logs} isLoading={isLoading} />

      {/* Filters */}
      <Card
        className="border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-800/50"
        header={
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Filters
          </span>
        }
      >
        <AuditLogFilters
          params={params}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          onRemoveFilter={handleRemoveFilter}
          onPresetApply={handlePresetApply}
        />
      </Card>

      {/* Logs table */}
      <Card
        contentClassName="p-0"
        header={
          <div className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Activity log
            </span>
            {!isLoading && total > 0 && (
              <Badge variant="neutral">{total} records</Badge>
            )}
          </div>
        }
        footer={
          !isLoading && !error && total > 0 ? (
            <TablePaginationBar
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onLimitChange={handleLimitChange}
            />
          ) : undefined
        }
      >
        <AuditLogList
            logs={logs}
            total={total}
            page={page}
            limit={limit}
            totalPages={totalPages}
            isLoading={isLoading}
            error={error}
            onPageChange={handlePageChange}
            onViewDetail={handleViewDetail}
            onClearFilters={handleResetFilters}
            onRetry={() => refetch()}
            hidePagination
          />
      </Card>

      <AuditLogDetailModal
        log={selectedLog ?? null}
        isLoading={detailLoading}
        error={detailError}
        open={selectedId !== null}
        onClose={handleCloseDetail}
      />
    </div>
    </PageShell>
  )
}

AuditLogsPage.displayName = 'AuditLogsPage'
